import { ScreenerData } from "./screeners/types";
import type { SurveyId } from "@/backend/db/drizzle/types/idTypes";

// Outcome summary of one spend-report ingest, stored on the report row.
// Covers the projects with unattributed sessions at publish time (new rows
// plus previously unpaired spend), each recomputed from its full ingested
// history; fully attributed projects keep their published state and are not
// re-reported here.
export type UserInterviewsCostReportSummary = {
  /** Rows in this file. */
  rowCount: number;
  // Sessions across the processed projects (their full histories); the
  // denominator for the match rate. Smaller than rowCount when a cumulative
  // re-export only touches a few projects.
  processedRowCount: number;
  projectCount: number;
  resolvedProjectCount: number;
  matchedCount: number;
  publishedTotalCents: number;
  /** Portion of publishedTotalCents that is our per-session platform fee. */
  feeTotalCents: number;
  // Sessions that published nothing, broken down by why.
  unattributedRows: {
    noTargetGroup: { count: number; totalCents: number };
    unpaired: { count: number; totalCents: number };
    participantsFetchFailed: { count: number; totalCents: number };
  };
  /** Paid participants across the processed projects (API "complete"). */
  paidParticipantCount: number;
  // Paid participants whose iid matched no response at all.
  paidWithoutResponseCount: number;
  // Paid participants whose iid matched more than one response; skipped.
  ambiguousParticipantCount: number;
  // Paid responses beyond what the export billed; nothing published for them.
  extraResponseCount: number;
  // Projects whose sessions were not all the same amount; their rows pair
  // with responses by completion order instead of exactly.
  variableAmountProjectCount: number;
  /** YYYY-MM-DD payout date range covered by the file. */
  minDate: string;
  maxDate: string;
  // Largest unattributed spend per User Interviews project, capped.
  topUnmatchedProjects: {
    projectId: string;
    projectName: string;
    surveyId: SurveyId | null;
    rowCount: number;
    totalCents: number;
    reason: "no_target_group" | "unpaired_rows" | "participants_fetch_failed";
  }[];
};

export type PaginatedResult = {
  links: {
    self: string;
    next?: string;
    prev?: string;
    last: string;
    first: string;
  };
  meta: {
    pagination: {
      currentPage: number;
      offset: number;
      perPage: number;
    };
  };
};

export type DeviceRequirement =
  | "device--computer_with_webcam"
  | "device--smartphone"
  | "device--tablet"
  | "device--computer"
  | "browser--google_chrome"
  | "browser--safari"
  | "browser--firefox"
  | "browser--microsoft_edge"
  | "browser--brave"
  | "browser--other_browser"
  | "os--chrome_os"
  | "os--mac_os"
  | "os--windows"
  | "os--gnu_linux_or_unix"
  | "os--other_os"
  | "recording--audio"
  | "recording--video"
  | "recording--screen";

export type ParticipantStatus =
  | "no_show"
  | "task_started"
  | "task_submitted"
  | "complete"
  | "fraudulent"
  | "approved" // should only ever be read
  // should not use, here for completeness
  | "rejected"
  | "qualified"
  | "unsorted"
  | "qualified_needs_review"
  | "prequalified"
  | "unqualified";

export type ParticipantAttributeType = "characteristics" | "screener_responses";

export type ParticipantAttributes = {
  characteristics: {
    name: string;
    slug: string;
    type:
      | "boolean"
      | "string"
      | "date"
      | "integer"
      | "decimal"
      | "multi_select"
      | "single_select"
      | "single_select_with_other";
    value: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }[];
  firstName: string;
  lastName: string;
  screenerResponses: {
    question: string;
    response: string;
  }[];
  source: string;
  status: ParticipantStatus;
};

export type ParticipantId = string & { readonly __brand: unique symbol };

export type ParticipantResponse = {
  type: "recruitParticipant";
  id: ParticipantId;
  attributes: ParticipantAttributes;
  relationships: {
    recruit: {
      data: {
        id: RecruitId;
        type: "recruit";
      };
    };
  };
};

export type UpdateParticipantRequest = {
  data: {
    attributes: {
      status: ParticipantStatus;
    };
  };
  fields: {
    participant: ParticipantAttributeType[];
  };
};

export type RecruitCriteria =
  | Record<
      SearchableCharacteristicType,
      | {
          $eq: string;
        }
      | {
          $in: string[];
        }
    >
  | {
      "integer--age": {
        $gte?: number;
        $lte?: number;
      };
    };

export type RecruitAttributes = {
  /** Compensation amount in USD */
  compensationAmount: number;
  requireApproval: boolean;
  numParticipants: number;
  internalDescription?: string;
  internalName?: string;
  publicDescription: string;
  publicTitle: string;
  taskDetails: {
    completionInterval: number;
    deadline: string; // ISO 8601 date string
    taskUrl: string;
    taskDuration: number;
    timeLimitEnabled: true;
  };
  criteria: RecruitCriteria[];
  deviceRequirements: DeviceRequirement[];
  preparationInstructions?: string;
  screener: ScreenerData;
  status: "paused" | "active" | "closed";
};

export type RecruitCreateRequest = {
  data: {
    type: "recruit";
    attributes: RecruitAttributes;
  };
};

export type RecruitUpdateRequest = {
  data: {
    type: "recruit";
    id: RecruitId;
    attributes: RecruitUpdatableAttributes;
  };
};

export type RecruitFeasibilityRequest = {
  data: {
    type: "recruit_feasibility";
    attributes: {
      criteria: RecruitCriteria[];
    };
  };
};

export type RecruitUpdatableAttributes = Pick<
  RecruitAttributes,
  | "compensationAmount"
  | "internalName"
  | "internalDescription"
  | "numParticipants"
  | "preparationInstructions"
  | "publicDescription"
  | "publicTitle"
  | "taskDetails"
  | "screener"
>;

export type RecruitId = string & { readonly __brand: unique symbol };

export type RecruitResponse = {
  data: {
    type: "recruit";
    id: RecruitId;
    attributes: Omit<
      RecruitAttributes,
      "screener" | "deviceRequirements" | "criteria"
    > & {
      createdAt: string;
    };
    links: {
      workspace: string;
    };
  };
};

export type RecruitReach =
  | "Very Narrow" // 0-24
  | "Narrow" // 25-99
  | "Moderate" // 100-499
  | "Broad" // 500-1999
  | "Very Broad"; // 2000+

export type RecruitFeasibilityResponse = {
  data: {
    type: "recruit_feasibility";
    id: string;
    attributes: {
      maxExpectedApplicants: number;
      minRangeApplicants: number;
      maxRangeApplicants: number;
      reach: RecruitReach;
    };
  };
};

export type CharacteristicCategory =
  | "Demographics"
  | "Technology and Online Behavior"
  | "Work"
  | "Finance";

export const CharacteristicTypeValues = [
  "integer--age",
  "multi_select--browsers",
  "multi_select--children",
  "multi_select--computer_operating_system",
  "multi_select--employment_status",
  "multi_select--skill",
  "multi_select--smartphone_operating_system",
  "multi_select--tablet_operating_system",
  "multi_select--type_of_income",
  "single_select--company_size",
  "single_select--computer_with_a_webcam",
  "single_select--country",
  "single_select--gender",
  "single_select--home_owner",
  "single_select--household_income_in_usd",
  "single_select--industry",
  "single_select--level_of_education",
  "single_select--living_situation",
  "single_select--marital_status",
  "single_select--occupation",
  "single_select--region",
  "single_select--seniority",
  "single_select--small_business_owner",
  "single_select_with_other--race_ethnicity",
  "multi_select--assistive_technologies_and_custom_settings",
  "multi_select--digital_design_prototyping_tools",
  "multi_select--disabilities",
  "multi_select--food_delivery_services",
  "multi_select--gig_or_contract_occupation_s",
  "multi_select--health_and_wellness_apps_platforms",
  "multi_select--health_insurance_type_s",
  "multi_select--online_banking_financial_management_tools",
  "multi_select--online_learning_educational_platforms",
  "multi_select--online_marketplace_platforms",
  "multi_select--online_streaming_services",
  "multi_select--online_travel_booking_services",
  "multi_select--other_language_s_spoken_fluently",
  "multi_select--pets_owned",
  "multi_select--productivity_project_management_software",
  "multi_select--smart_home_and_gaming_devices",
  "multi_select--social_networking_platforms",
  "multi_select--traditional_banks",
  "multi_select--vehicles_currently_owned_or_leased",
  "single_select--english_language_proficiency_-_speaking",
  "single_select--military_service_status",
  "single_select--neighborhood_type",
  "single_select--work_setting",
] as const;

export type CharacteristicType = (typeof CharacteristicTypeValues)[number];

export type SearchableCharacteristicType = Exclude<
  CharacteristicType,
  "integer--age"
>;

export type CharacteristicId = string & { readonly __brand: unique symbol };

export type Characteristic = {
  id: CharacteristicId;
  type: "characteristicOption";
  attributes: {
    name: string;
  };
};
export type CharacteristicSearchResult = PaginatedResult & {
  data: Characteristic[];
};
